import { useState } from 'react';
import { AddLicensesStep } from './add-licenses-step';
import {
  AbstractCurrentSubscriptionModel,
  BillingInformationModel,
  CheckoutModel,
  HubSpotCompany,
  PendingOrder,
  StripeAchPaymentSourceModel,
} from '../../api/billing-types';
import { ReviewStep } from './review-step';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useMutation } from '@tanstack/react-query';
import BulkAddLicensesDialog from './bulk-add-license-dialog';
import { ChoosePaymentMethodStep } from './choose-payment-method-step';
import { checkout, uploadPurchaseOrderToDrive } from '../../api/billing-api';
import { useAnalytics } from '../../shared/analytics/create-events';
import { handleErrors } from '../../utils/errors';
import * as analytics from '../../shared/analytics/analytics.json';
import { ModuleList } from '../../shared/analytics/module-list';
import { useUserSettings } from '../../utils/use-user-settings';
import { Messages } from '../../utils/message-constants';
import { useStripe, useElements, CardElement } from '@stripe/react-stripe-js';
import { useStore } from '../state/zustand-store';
import SimpleFormPage, { getSchemaKeys } from '../../shared/simple-form-page';
import { pagesConfig } from '../../shared/pages-config';
import SharedFormStepper from '../../shared/shared-form-stepper';
import { useNavigate } from 'react-router-dom';
import UnsavedChangesDialog, {
  useUnsavedChanges,
} from '../../shared/unsaved-changes-dialog';

import { z, ZodObject, ZodRawShape } from 'zod';

import { zodResolver } from '@hookform/resolvers/zod';
import ChangeLogDialog from '../../shared/change-log-dialog';
import {
  BillingRoles,
  isAbleToSeeUIElement,
} from '../../utils/roles/client-roles';
import Box from '@mui/material/Box';

export const StripeCustomerSchema = z.object({
  id: z.string(),
});

// StripePaymentSourceMetadataModel
export const StripePaymentSourceMetadataSchema = z.object({
  createdDate: z.string(),
  createdBy: z.string().nullable(),
  nickName: z.string().nullable(),
  autoPayActivationDate: z.number(),
  isAutoPaySource: z.boolean(),
  hasMultiUseMandate: z.boolean(),
});

export const StripePaymentSourceSchema = z.object({
  id: z.string(),
  address_city: z.string(),
  address_country: z.string(),
  address_line1: z.string(),
  address_state: z.string(),
  address_zip: z.string(),
  brand: z.string(),
  country: z.string(),
  customer: StripeCustomerSchema,
  metadata: StripePaymentSourceMetadataSchema.optional(),
  name: z.string(),
  object: z.string(),
  last4: z.string(),
  expireString: z.string(),
  cardDetailsString: z.string(),
  cardDetails2String: z.string(),
  showWarning: z.boolean(),
  stripePaymentSourceType: z.enum(['CreditCard', 'Ach']),
});

export const StripeAchPaymentSourceSchema = StripePaymentSourceSchema.merge(
  z.object({
    bank_name: z.string(),
    routing_number: z.string(),
    account_number: z.string(),
    account_holder_name: z.string(),
    account_holder_type: z.string(),
    status: z.string(),
  })
);

export const PromevoSkuPairSchema = z.object({
  promevoSku: z.string(),
});

export const AddressSchema = z.object({
  streetAddress: z.string(),
  streetAddress2: z.string(),
  city: z.string(),
  state: z.string(),
  country: z.string(),
  zipCode: z.string(),
  phone: z.string(),
});

export const LineItemSchema = z.object({
  price: z.number(),
  name: z.string(),
  sku: PromevoSkuPairSchema,
  qty: z.number(),
  purchaseLicenseCount: z.number(),
  timeout: z.number(),
});

export const AddLicenseSchema = z.object({
  items: z.array(LineItemSchema),
});

export const ChoosePaymentMethodSchema = z.object({
  purchaseOrderNumber: z.string(),
  purchaseOrderUpload: z.file().nullable().optional(),
  stripePaymentType: z.enum([
    'Invoice',
    'PurchaseOrder',
    'StripeCreditCard',
    'StripeBankAccount',
  ]),
  stripePaymentMethodId: z.string(),
  stripeCustomerId: z.string().nullable().optional(),
  newStripeCreditCard: StripeAchPaymentSourceSchema.optional(),
  stripeTokenId: z.string().optional(),
  achAgreementAccepted: z.boolean().optional(),
});

export const ReviewSchema = z.object({
  billingType: z.enum([
    'annualPrepay',
    'annualOnBillDate',
    'monthlyPrepay',
    'monthlyPostPay',
    'monthlyOnBillDate',
    'semiAnnual',
  ]),
  country_id: z.enum(['us', 'canada']),
  shippingAddress: AddressSchema,
  isTaxExempt: z.boolean(),
  email: z.string(),
});

export const CheckoutSchema = AddLicenseSchema.and(ChoosePaymentMethodSchema)
  .and(ReviewSchema)
  .superRefine((data, ctx) => {
    if (!(data.items && data.items[0].qty > 0)) {
      ctx.addIssue({
        code: 'custom',
        message: 'Must be greater than 0.',
        path: ['items', '0', 'qty'],
      });
    }

    if (data.stripePaymentMethodId === 'PurchaseOrder') {
      if (!data.purchaseOrderNumber || data.purchaseOrderNumber.trim() === '') {
        ctx.addIssue({
          code: 'custom',
          message: 'Purchase order number is required.',
          path: ['purchaseOrderNumber'],
        });
      }
      if (!data.purchaseOrderUpload) {
        ctx.addIssue({
          code: 'custom',
          message: 'Purchase order upload is required.',
          path: ['purchaseOrderUpload'],
        });
      }
    }
    if (data.stripePaymentMethodId === 'NewStripeCreditCard') {
      const cc = data.newStripeCreditCard;
      if (!cc) {
        ctx.addIssue({
          code: 'custom',
          message: 'Credit card information is required.',
          path: ['newStripeCreditCard'],
        });
        return;
      }

      if (!cc.account_holder_name || cc.account_holder_name.trim() === '') {
        ctx.addIssue({
          code: 'custom',
          message: 'Please provide a cardholder name.',
          path: ['newStripeCreditCard', 'account_holder_name'],
        });
      }

      if (!cc.address_line1 || cc.address_line1.trim() === '') {
        ctx.addIssue({
          code: 'custom',
          message: 'Please provide a street address.',
          path: ['newStripeCreditCard', 'address_line1'],
        });
      }

      if (!cc.address_city || cc.address_city.trim() === '') {
        ctx.addIssue({
          code: 'custom',
          message: 'Please provide a city.',
          path: ['newStripeCreditCard', 'address_city'],
        });
      }

      if (!cc.address_state || cc.address_state.trim() === '') {
        ctx.addIssue({
          code: 'custom',
          message: 'Please provide a state/province.',
          path: ['newStripeCreditCard', 'address_state'],
        });
      }

      if (!cc.address_country || cc.address_country.trim() === '') {
        ctx.addIssue({
          code: 'custom',
          message: 'Please provide a country.',
          path: ['newStripeCreditCard', 'address_country'],
        });
      }
    }
  });

export type CheckoutModelSchema = z.infer<typeof CheckoutSchema>;

interface BillingFormPageProps {
  subscription: AbstractCurrentSubscriptionModel;
  hubSpotCompany: HubSpotCompany;
  billingInfoModel: BillingInformationModel;
  pendingPurchaseOrders: (PendingOrder | undefined)[];
  totalPurchasedWorkspaceLicenses: number;
  totalPurchasedAULicenses: number;
  bundledGpanelSubscription: AbstractCurrentSubscriptionModel | null;
  isGoogleArchived: boolean;
}

export default function BillingFormPage({
  subscription,
  hubSpotCompany,
  billingInfoModel,
  pendingPurchaseOrders,
  totalPurchasedWorkspaceLicenses,
  totalPurchasedAULicenses,
  bundledGpanelSubscription,
  isGoogleArchived,
}: BillingFormPageProps) {
  const { createEvent } = useAnalytics();
  const navigate = useNavigate();
  const { currentTier, isPlatform, changeLogRoles } = useUserSettings();
  const [openBulkAddDialog, setOpenBulkAddDialog] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [isLoadingStripeToken, setIsLoadingStripeToken] = useState(false);
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');

  const stripe = useStripe();
  const elements = useElements();
  const { setSnackbar } = useStore();

  const totalExistingWorkspaceLicenses = isGoogleArchived
    ? totalPurchasedAULicenses
    : totalPurchasedWorkspaceLicenses;

  const [agreeToTermsCheckbox, setAgreeToTermsCheckbox] = useState(false);
  const steps =
    subscription.billingType === 'monthlyPrepay'
      ? [
          {
            label: 'Add License',
            schema: AddLicenseSchema,
          },
          {
            label: 'Review',
          },
        ]
      : [
          {
            label: 'Add License',
            schema: AddLicenseSchema,
          },
          {
            label: 'Choose Payment Method',
            schema: ChoosePaymentMethodSchema,
          },
          {
            label: 'Review',
          },
        ];

  const isMonthlyPrepay = subscription.billingType === 'monthlyPrepay';
  const isgPanelSubMonthlyPrepay =
    bundledGpanelSubscription?.billingType === 'monthlyPrepay';

  function getBundleLineItem() {
    const hasEssentials = currentTier === 'essentials';
    const isSubPlatform = isPlatform;
    const shouldBundlegPanelWithWorkspace = !hasEssentials && !isSubPlatform;

    if (!bundledGpanelSubscription) {
      return;
    }

    if (shouldBundlegPanelWithWorkspace) {
      return {
        price: isgPanelSubMonthlyPrepay
          ? 0
          : bundledGpanelSubscription.proratedPricePerUnit,
        name: bundledGpanelSubscription.productModel.productName,
        sku: bundledGpanelSubscription.proratedSkuPair,
        qty: 0,
        purchaseLicenseCount: bundledGpanelSubscription.totalPurchasedLicenses,
        timeout: bundledGpanelSubscription.timeout,
      };
    }
  }

  const bundleItem = getBundleLineItem();

  const {
    control,
    handleSubmit,
    formState: { errors, isValidating, isSubmitting, isDirty },
    trigger,
    reset,
    watch,
    setValue,
    getValues,
    setError,
  } = useForm<CheckoutModelSchema>({
    resolver: zodResolver(CheckoutSchema),
    values: {
      billingType: subscription.billingType,
      items: [
        {
          price: isMonthlyPrepay ? 0 : subscription.proratedPricePerUnit,
          name: subscription.productModel.productName,
          sku: subscription.proratedSkuPair,
          qty: 0,
          purchaseLicenseCount: subscription.totalPurchasedLicenses,
          timeout: subscription.timeout,
        },
        ...(bundleItem ? [bundleItem] : []),
      ],
      country_id: billingInfoModel.qbInstance,
      shippingAddress: billingInfoModel.shippingAddress,
      isTaxExempt: hubSpotCompany.properties.isTaxExempt,
      email: billingInfoModel.customerInfoModel?.customerEmail || '',
      purchaseOrderNumber: '',
      purchaseOrderUpload: null,
      stripePaymentType: 'Invoice',
      stripePaymentMethodId: 'Invoice',
      stripeCustomerId:
        billingInfoModel.customerInfoModel?.stripeCustomerId || '',
      newStripeCreditCard: {
        id: '',
        customer: { id: '' },
        brand: '',
        country: '',
        name: '',
        object: '',
        last4: '',
        expireString: '',
        cardDetailsString: '',
        cardDetails2String: '',
        showWarning: false,
        stripePaymentSourceType: 'CreditCard',
        bank_name: '',
        routing_number: '',
        account_number: '',
        account_holder_name: '',
        account_holder_type: '',
        status: '',
        address_country: 'United States',
        address_line1: '',
        address_city: '',
        address_state: '',
        address_zip: '',
      },
      stripeTokenId: '',
    },
  });

  const { mutate, isPending } = useMutation({
    mutationFn: ({
      data,
      newCreditCard,
    }: {
      data: CheckoutModel;
      newCreditCard: boolean;
    }) => checkout(data, newCreditCard, changeLogMessage),
    onSuccess: (returnedData) => {
      setOpenChangeLogDialog(false);
      setChangeLogMessage('');
      setActiveStep(0);
      reset();
      bypass();
      navigate(
        `/${pagesConfig.billing.link}/${pagesConfig.billingSubscriptions.link}/${returnedData}`
      );
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage: Messages.Errors.Network.Adding('licenses'),
      });
    },
    onSettled: (returnedData, _error, variables) => {
      createEvent(analytics.billing.subscriptions.add_licenses, {
        module: ModuleList.modules.billing.name,
        section: ModuleList.modules.billing.sections.subscriptions,
        stripePaymentType: variables.data.stripePaymentType,
        items: variables.data.items.map((item) => ({
          name: item.name,
          qty: item.qty,
          sku: item.sku.promevoSku,
        })),
        orderNumber: returnedData || '',
      });
    },
  });

  const {
    mutate: uploadPurchaseOrderMutate,
    isPending: isUploadingPurchaseOrder,
  } = useMutation({
    mutationFn: (data: {
      purchaseOrderNumber: string;
      purchaseOrderUpload: File;
    }) =>
      uploadPurchaseOrderToDrive(
        data.purchaseOrderNumber,
        data.purchaseOrderUpload
      ),
    onSettled: () => {
      createEvent(analytics.billing.subscriptions.upload_purchase_order, {
        module: ModuleList.modules.billing.name,
        section: ModuleList.modules.billing.sections.subscriptions,
      });
    },
  });

  async function getStripeTokenForCC(
    data: Partial<StripeAchPaymentSourceModel>
  ) {
    if (!stripe || !elements) {
      return;
    }
    const cardElement = elements.getElement(CardElement);
    if (!cardElement) {
      return;
    }

    elements.submit();

    const result = await stripe?.createToken(cardElement, {
      name: data.account_holder_name,
      address_line1: data.address_line1,
      address_city: data.address_city,
      address_state: data.address_state,
      address_zip: data.address_zip,
      address_country: data.address_country,
    });

    if (result?.error) {
      setSnackbar({
        open: true,
        severity: 'error',
        message:
          result.error.message ||
          'There was an issue generating a stripe token.',
      });
    } else {
      return result?.token?.id;
    }
  }

  const selectedPaymentMethodId = watch('stripePaymentMethodId');
  const selectedMethod = billingInfoModel.stripeSourceModels.find(
    (method) => method.id === selectedPaymentMethodId
  );

  function handleSubmitMutate(data: CheckoutModelSchema) {
    if (data.stripePaymentType === 'StripeBankAccount') {
      const selectedMethod = billingInfoModel.stripeSourceModels.find(
        (method) => method.id === data.stripePaymentMethodId
      );

      const needsAgreement =
        String(selectedMethod?.metadata?.hasMultiUseMandate) !== 'true';
      if (needsAgreement && !data.achAgreementAccepted) {
        setError('achAgreementAccepted', {
          type: 'manual',
          message: 'You must agree to the ACH authorization mandate.',
        });
        if (!isMonthlyPrepay) {
          setActiveStep(1);
        }
        return;
      }
    }

    if (data.items[1] && data.items[1].qty === 0) {
      data.items.splice(1, 1);
    }

    if (
      data.stripePaymentMethodId === 'Invoice' ||
      data.stripePaymentMethodId === 'PurchaseOrder'
    ) {
      data.stripePaymentType = data.stripePaymentMethodId;
      data.stripePaymentMethodId = '';
    }

    const isNewCreditCard =
      data.stripePaymentMethodId === 'NewStripeCreditCard';

    if (isNewCreditCard && data.newStripeCreditCard) {
      data.stripePaymentMethodId = data.stripeTokenId || '';
      data.stripePaymentType = 'StripeCreditCard';
      data.stripeCustomerId = null;
    }

    if (data.purchaseOrderUpload) {
      uploadPurchaseOrderMutate({
        purchaseOrderNumber: data.purchaseOrderNumber,
        purchaseOrderUpload: data.purchaseOrderUpload,
      });
    }

    delete data.newStripeCreditCard;
    delete data.stripeTokenId;
    delete data.purchaseOrderUpload;

    mutate({ data, newCreditCard: isNewCreditCard });
  }

  const onSubmit: SubmitHandler<CheckoutModelSchema> = async (data) => {
    if (
      isAbleToSeeUIElement(changeLogRoles, BillingRoles.canAddLicenses.value) ||
      (isAbleToSeeUIElement(
        changeLogRoles,
        BillingRoles.canUseSavedCardsForLicensePurchases.value
      ) &&
        selectedMethod)
    ) {
      setOpenChangeLogDialog(true);
    } else {
      handleSubmitMutate(data);
    }
  };

  const additionalLicenseCount = watch().items[0].qty || 0;

  const [stepCompleted, setStepCompleted] = useState([true, false, false]);

  const {
    showDialog: showUnsavedChangesDialog,
    handleConfirm: handleUnsavedConfirm,
    handleCancel: handleUnsavedCancel,
    bypass,
    isCancelAction,
  } = useUnsavedChanges(isDirty);

  const handleCancel = () => {
    navigate(
      `/${pagesConfig.billing.link}/${pagesConfig.billingSubscriptions.link}`,
      { state: { fromCancelButton: true } }
    );
  };

  const handleNext = async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    if (
      activeStep === 0 &&
      additionalLicenseCount > 49 &&
      !hubSpotCompany.properties.enable_bulk_purchasing
    ) {
      setOpenBulkAddDialog(true);
    } else if (activeStep === 1 && !isMonthlyPrepay) {
      const fields = steps[activeStep].schema
        ? getSchemaKeys(steps[activeStep].schema as ZodObject<ZodRawShape>)
        : [];
      const isValid = await trigger(fields as never);

      if (!isValid) {
        return;
      }

      const currentValues = getValues();
      if (currentValues.stripePaymentType === 'StripeBankAccount') {
        const selectedMethod = billingInfoModel.stripeSourceModels.find(
          (method) => method.id === currentValues.stripePaymentMethodId
        );
        const needsAgreement =
          String(selectedMethod?.metadata?.hasMultiUseMandate) !== 'true';
        if (needsAgreement && !currentValues.achAgreementAccepted) {
          setError('achAgreementAccepted', {
            type: 'manual',
            message: 'You must agree to the ACH authorization mandate.',
          });
          return;
        }
      }

      const isNewCreditCard =
        currentValues.stripePaymentMethodId === 'NewStripeCreditCard';
      const newStripeCreditCard = currentValues.newStripeCreditCard;

      if (isNewCreditCard && newStripeCreditCard) {
        setIsLoadingStripeToken(true);
        try {
          const tokenId = await getStripeTokenForCC(newStripeCreditCard);
          if (tokenId) {
            setValue('stripeTokenId', tokenId);
          } else {
            return;
          }
        } finally {
          setIsLoadingStripeToken(false);
        }
      }

      setActiveStep((prevState) => prevState + 1);
      setStepCompleted((prevState) => {
        const newCompleted = [...prevState];
        newCompleted[activeStep] = true;
        return newCompleted;
      });
    } else if (activeStep === steps.length - 1) {
      //
    } else {
      const fields = steps[activeStep].schema
        ? getSchemaKeys(steps[activeStep].schema as ZodObject<ZodRawShape>)
        : [];
      const isValid = await trigger(fields as never);

      if (isValid) {
        setActiveStep((prevState) => prevState + 1);
      }
    }
  };

  const formStepperProps = {
    activeStep,
    setActiveStep,
    steps,
    errors,
    trigger,
    stepCompleted,
    setStepCompleted,
  };

  const currentStep = steps[activeStep];

  return (
    <>
      <SimpleFormPage
        title={pagesConfig.addLicense.name}
        stepper={<SharedFormStepper {...formStepperProps} />}
        {...formStepperProps}
        content={
          <Box
            sx={{
              width: '100%',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {currentStep.label === 'Add License' && (
              <AddLicensesStep
                control={control}
                watch={watch}
                subscription={subscription}
                errors={errors}
                pendingOrders={pendingPurchaseOrders}
                bundledGpanelSubscription={bundledGpanelSubscription}
                setValue={setValue}
                totalExistingWorkspaceLicenses={totalExistingWorkspaceLicenses}
              />
            )}
            {currentStep.label === 'Choose Payment Method' && (
              <ChoosePaymentMethodStep
                billingInfoModel={billingInfoModel}
                hubSpotCompany={hubSpotCompany}
                control={control}
                watch={watch}
                setValue={setValue}
                errors={errors}
                getValues={getValues}
                trigger={trigger}
              />
            )}
            {currentStep.label === 'Review' && (
              <ReviewStep
                subscription={subscription}
                agreeToTermsCheckbox={agreeToTermsCheckbox}
                setAgreeToTermsCheckbox={setAgreeToTermsCheckbox}
                watch={watch}
                bundledGpanelSubscription={bundledGpanelSubscription}
              />
            )}
          </Box>
        }
        handleNext={handleNext}
        handleDiscard={handleCancel}
        handleSubmit={handleSubmit(onSubmit)}
        isDisableSubmit={
          activeStep === steps.length - 1 && !agreeToTermsCheckbox
        }
        submitButtonText={pagesConfig.addLicense.name}
        isLoadingSubmit={
          isValidating ||
          isSubmitting ||
          isPending ||
          isUploadingPurchaseOrder ||
          isLoadingStripeToken
        }
      />
      <UnsavedChangesDialog
        isCancelAction={isCancelAction}
        entityName="Billing Subscription"
        pageMode="create"
        open={showUnsavedChangesDialog}
        handleConfirm={handleUnsavedConfirm}
        handleClose={handleUnsavedCancel}
      />
      {openBulkAddDialog ? (
        <BulkAddLicensesDialog
          open={openBulkAddDialog}
          handleClose={() => setOpenBulkAddDialog(false)}
          subscription={subscription}
          additonalLicenseCount={additionalLicenseCount}
        />
      ) : null}
      <ChangeLogDialog
        open={openChangeLogDialog}
        handleClose={() => setOpenChangeLogDialog(false)}
        changeLogMessage={changeLogMessage}
        setChangeLogMessage={setChangeLogMessage}
        mutate={() => {
          handleSubmitMutate(getValues());
        }}
        mutateLoading={isPending}
      />
    </>
  );
}
